import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const UploadPhotoModal = ({ onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [metadata, setMetadata] = useState({
    caption: '',
    weight: '',
    bodyFatPercentage: '',
    category: 'front',
    takenAt: new Date()?.toISOString()?.split('T')?.[0]
  });
  const [isLoading, setIsLoading] = useState(false);

  const categoryOptions = [
    { value: 'front', label: 'Front View' },
    { value: 'side', label: 'Side View' },
    { value: 'back', label: 'Back View' }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader?.result);
      };
      reader?.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!file) return;

    setIsLoading(true);
    await onUpload(file, {
      ...metadata,
      weight: metadata?.weight ? parseFloat(metadata?.weight) : null,
      bodyFatPercentage: metadata?.bodyFatPercentage ? parseFloat(metadata?.bodyFatPercentage) : null
    });
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-foreground">Upload Progress Photo</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Photo</label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              {preview ? (
                <div className="space-y-4">
                  <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                  <Button type="button" variant="outline" onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}>
                    Change Photo
                  </Button>
                </div>
              ) : (
                <div>
                  <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Click to upload or drag and drop</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <Button type="button" as="span">
                      Select Photo
                    </Button>
                  </label>
                </div>
              )}
            </div>
          </div>

          <Input
            label="Caption (optional)"
            value={metadata?.caption}
            onChange={(e) => setMetadata(prev => ({ ...prev, caption: e?.target?.value }))}
            placeholder="Describe your progress"
          />

          <Select
            label="Photo Category"
            value={metadata?.category}
            onChange={(e) => setMetadata(prev => ({ ...prev, category: e?.target?.value }))}
            options={categoryOptions}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Weight (lbs)"
              type="number"
              step="0.1"
              value={metadata?.weight}
              onChange={(e) => setMetadata(prev => ({ ...prev, weight: e?.target?.value }))}
              placeholder="165.5"
            />

            <Input
              label="Body Fat %"
              type="number"
              step="0.1"
              value={metadata?.bodyFatPercentage}
              onChange={(e) => setMetadata(prev => ({ ...prev, bodyFatPercentage: e?.target?.value }))}
              placeholder="18.5"
            />
          </div>

          <Input
            label="Date Taken"
            type="date"
            value={metadata?.takenAt}
            onChange={(e) => setMetadata(prev => ({ ...prev, takenAt: e?.target?.value }))}
          />

          <div className="flex gap-3 pt-4">
            <Button type="submit" loading={isLoading} fullWidth disabled={!file}>
              Upload Photo
            </Button>
            <Button type="button" variant="outline" onClick={onClose} fullWidth>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadPhotoModal;